import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import ApiUrl from "../Environment/ApiUrl";
import "../../assets/css/SalesBill.css";
import { useToast } from "../Toast/ToastContext";

export default function Bill() {
  const { sales_no } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salesData, setSalesData] = useState(null);

  useEffect(() => {
    if (state?.salesData) {
      const sd = state.salesData;
      setSalesData({
        ...sd,
        item_list: (sd.item_list || []).map((item) => ({
          ...item,
          making_charges: item.making_charges ?? item.mc ?? "0",
          making_wastage: item.making_wastage ?? item.mw ?? "0",
          making_wastage_percentage: item.making_wastage_percentage ?? item.mw_percentage ?? "0",
        })),
      });
      setLoading(false);
    } else if (sales_no) {
      fetchSalesData();
    } else {
      setError("No sales data provided");
      setLoading(false);
    }
  }, [sales_no, state]);

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${ApiUrl}/Api/sales.php?sales_no=${sales_no}`);
      console.log("API Response:", response.data);
      
      const filteredData = response.data.data?.find((item) => item.sales_no === sales_no);
      if (filteredData) {
        console.log("Filtered Sales Data:", filteredData);
        setSalesData(filteredData);
      } else {
        setError(`No sales data found for sales number: ${sales_no}`);
      }
    } catch (error) {
      console.error("API error:", error);
      setError("Failed to fetch sales data");
      toast.error(`Failed to fetch sales data: ${error.response?.data?.message || error.message}`, 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  // Add near numberToWords/formatDate
const formatOrDash = (value, decimals = 2) => {
  const num = parseFloat(value) || 0;
  return num > 0 ? `₹${num.toFixed(decimals)}` : "-";
};

  const numberToWords = (num) => {
    if (isNaN(num) || num === null || num === undefined) return "";
    const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
    const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
    num = parseFloat(num);
    if (num === 0) return "Zero Only";
    const isNegative = num < 0;
    num = Math.abs(num);
    const conv = (n) => {
      if (n < 20) return ones[n];
      return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    };
    let result = "";
    let intPart = Math.floor(num);
    const decimalPart = Math.round((num - intPart) * 100);
    if (intPart >= 100000) { result += conv(Math.floor(intPart / 100000)) + " Lakh "; intPart %= 100000; }
    if (intPart >= 1000)  { result += conv(Math.floor(intPart / 1000)) + " Thousand "; intPart %= 1000; }
    if (intPart >= 100)   { result += conv(Math.floor(intPart / 100)) + " Hundred "; intPart %= 100; }
    if (intPart > 0) result += conv(intPart);
    if (decimalPart > 0) result += " and " + conv(decimalPart) + " Paise";
    return (isNegative ? "Minus " : "") + result.trim() + " Only";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { year: "numeric", month: "short", day: "2-digit" };
    return date.toLocaleDateString("en-US", options);
  };

  const actionTypeAbbr = (actionType) => {
    const map = {
      "new": "New",
      "exchange": "Exc",
      "po-sale": "PO",
      "old sales": "OS",
    };
    return map[(actionType || "").trim().toLowerCase()] || actionType || "";
  };

  const isExchangeItem = (item) =>
    (item?.action_type || "").trim().toLowerCase() === "exchange";

  // Format a signed rupee amount as "-₹500.00" / "₹500.00"
  const formatSigned = (value) => {
    const num = parseFloat(value) || 0;
    return `${num < 0 ? "-" : ""}₹${Math.abs(num).toFixed(2)}`;
  };

  // Calculate all totals with exchange logic.
  // Exchange items represent the value of OLD items being credited back
  // to the customer, so their amount must be SUBTRACTED from the total
  // of everything else (New / Po-Sale / Old Sales items), not added.
  const calculateTotals = () => {
    if (!salesData?.item_list || salesData.item_list.length === 0) {
      return {
        totalQty: 0,
        totalWeight: 0,
        totalMaking: 0,
        totalWastage: 0,
        totalStoneValue: 0,
        newTotal: 0,
        exchangeTotal: 0,
        netAmountTotal: 0,
        hasNewItems: false,
        hasExchangeItems: false,
      };
    }

    let totalQty = 0;
    let totalWeight = 0;
    let totalMaking = 0;
    let totalWastage = 0;
    let totalStoneValue = 0;
    let newTotal = 0;
    let exchangeTotal = 0;

    salesData.item_list.forEach((item) => {
      const qty = parseInt(item.quantity) || 0;
      const weight = parseFloat(item.weight) || 0;
      const amount = parseFloat(item.amount) || 0;
      const making = parseFloat(item.making_charges) || 0;
      const wastage = parseFloat(item.making_wastage) || 0;
      const stoneValue = parseFloat(item.stone_value) || 0;

      totalQty += qty;
      totalWeight += weight;
      totalMaking += making;
      totalWastage += wastage;
      totalStoneValue += stoneValue;

      if (isExchangeItem(item)) {
        exchangeTotal += amount;
      } else {
        newTotal += amount;
      }
    });

    // Net amount actually owed by the customer: everything sold minus
    // whatever was credited back for exchanged (old) items.
    const netAmountTotal = newTotal - exchangeTotal;

    const hasNewItems = salesData.item_list.some((item) => !isExchangeItem(item));
    const hasExchangeItems = salesData.item_list.some((item) => isExchangeItem(item));

    return {
      totalQty,
      totalWeight,
      totalMaking,
      totalWastage,
      totalStoneValue,
      newTotal,
      exchangeTotal,
      netAmountTotal,
      hasNewItems,
      hasExchangeItems,
    };
  };

  const handleBackToEdit = () => {
    if (state?.isEstimate && state?.restore) {
      navigate("/add-sales", { state: { restore: state.restore } });
    } else {
      navigate("/sales");
    }
  };

  if (loading) return <div className="d-flex justify-content-center p-4">Loading sales data...</div>;
  if (error)   return <div className="d-flex justify-content-center p-4 text-danger">{error}</div>;
  if (!salesData) return <div className="d-flex justify-content-center p-4">No sales data available</div>;

  const {
    totalQty,
    totalWeight,
    totalMaking,
    totalWastage,
    totalStoneValue,
    netAmountTotal,
  } = calculateTotals();

  const formattedDate = formatDate(salesData.sales_date);
  const currentTime = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });

  // Net amount owed after subtracting exchange credit — used as the
  // starting point for discount / tax / round-off below.
  const subTotal = netAmountTotal;

  // Get discount from sales data
  const discountPercentage = parseFloat(salesData.discount) || 0;

  // Calculate TD (Discount) per item based on item amount.
  // Exchange items don't get a line-item discount — the discount applies
  // to what's being sold, not to the value credited back for old items.
  // NOTE: defined here (before netTotalBeforeTax) so Sub Total / Net Amount
  // use the SAME discount total as the per-row TD column and the items
  // table footer. Previously discountValue was (subTotal * discount%) —
  // i.e. on the net-of-exchange amount — while the table's TD column/
  // footer used the sum of each row's own discount (based on newTotal
  // only, before subtracting exchange). Those two numbers only matched
  // when there were no exchange items, so Sub Total / Net Amount silently
  // drifted from the table total whenever an exchange was involved.
  const calculateItemTD = (itemAmount) => {
    if (!discountPercentage || discountPercentage === 0) return 0;
    return (itemAmount * discountPercentage) / 100;
  };

  const totalTD = salesData.item_list?.reduce((sum, item) => {
    if (isExchangeItem(item)) return sum;
    const amount = parseFloat(item.amount) || 0;
    return sum + calculateItemTD(amount);
  }, 0) || 0;

  const discountValue = totalTD;
  const netTotalBeforeTax = subTotal - discountValue;

  // Get tax from sales data (this is actually GST/Tax)
  const taxPercentage = parseFloat(salesData.tax) || 0;
  const taxAmount = (netTotalBeforeTax * taxPercentage) / 100;
  
  // Round off
  const roundOff = parseFloat(salesData.round_off) || 0;
  
  // Grand Total
  const grandTotal = netTotalBeforeTax + taxAmount + roundOff;

  // Balance - show only if it exists and is greater than 0
  const balance = parseFloat(salesData.balance) || 0;
  const showBalance = balance > 0;

  // Get paid amount
  const paidAmount = parseFloat(salesData.paid) || 0;

  // Gold & Silver rates from the first item of each type
  const goldItem = salesData.item_list?.find(i => (i.category || "").toLowerCase() === "gold");
  const silverItem = salesData.item_list?.find(i => (i.category || "").toLowerCase() === "silver");
  const goldRate = goldItem?.rate;
  const silverRate = silverItem?.rate;

  // Payment type from payment history
  const paymentType = salesData.payment_history?.[0]?.payment_type || "N/A";

  // Determine which optional columns actually have data anywhere in the bill
const hasMaking = salesData.item_list?.some(
  (item) => (parseFloat(item.making_charges) || 0) > 0
);
const hasWastage = salesData.item_list?.some(
  (item) => (parseFloat(item.making_wastage) || 0) > 0
);
const hasStoneValue = salesData.item_list?.some(
  (item) => (parseFloat(item.stone_value) || 0) > 0
);
const hasDiscount = discountPercentage > 0; // already computed above, drives TD column

  return (
    <div className="bill-container">
      <button className="print-btn no-print" onClick={() => window.print()}>
        Print Bill
      </button>
      <button className="cancel-btn no-print" onClick={handleBackToEdit}>
        {state?.isEstimate ? "Back to Edit" : "Cancel"}
      </button>

      {/* Header */}
      <div className="header-section d-flex justify-content-between align-items-center">
        <div className="logo-container">
          <img
            src={require("../../assets/images/Final logo c .png")}
            alt="TIRUPUR Logo"
            className="logo"
            style={{ maxHeight: "80px" }}  />
        </div>
        <div className="company-info text-center">
          <h4 className="mb-1">TIRUPUR JEWELLERY</h4>
          <p className="mb-0">3/1, Thandan St, Shevapet, Salem, Tamil Nadu 636002</p>
          <p className="mb-0">Ph: 04272233807</p>
        </div>
        <div className="invoice-title">
          <p className="m-0 fw-bold">{state?.isEstimate ? "SALES ESTIMATE" : "SALES INVOICE"}</p>
        </div>
      </div>

      <hr className="divider" style={{marginTop:"-20px"}}/>

      {/* Customer + Invoice Info */}
      <div className="details-section">
        <div className="customer-info">
          <div className="customer-details">
            <p><strong>Customer:</strong> {salesData.customer_data?.name || salesData.customer_name || "N/A"}</p>
            <p><strong>Address:</strong> {salesData.customer_data?.address || salesData.address || "N/A"}</p>
            <p><strong>Cell:</strong> {salesData.customer_data?.mobile || salesData.mobile || "N/A"}</p>
            {/* {taxPercentage > 0 && <p><strong>GST:</strong> {taxPercentage}%</p>} */}
          </div>
        </div>
        <div className="invoice-info">
          <div className="info-row"><span>Date</span><span>: {formattedDate} / {currentTime}</span></div>
          <div className="info-row"><span>Bill No</span><span>: {salesData.sales_no}</span></div>
          {goldRate && <div className="info-row"><span>Gold</span><span>: ₹{parseFloat(goldRate).toFixed(2)}/g</span></div>}
          {silverRate && <div className="info-row"><span>Silver</span><span>: ₹{parseFloat(silverRate).toFixed(2)}/g</span></div>}
        </div>
      </div>

      {/* Items Table */}
      <div className="table-section">
        <table className="bill-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Product</th>
              <th>Qty</th>
              <th>Weight(g)</th>
              <th>Rate</th>
    {hasMaking && <th>MC</th>}
    {hasWastage && <th>Wastage</th>}
    {hasStoneValue && <th>Stone Value</th>}
    {hasDiscount && <th>TD (₹)</th>}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {salesData.item_list?.map((item, index) => {
              const itemAmount = parseFloat(item.amount) || 0;
              const isExchange = isExchangeItem(item);
              const itemTD = isExchange ? 0 : calculateItemTD(itemAmount);

              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    {item.item_name_tamil
                      ? `${item.item} / ${item.item_name_tamil}`
                      : item.item || "N/A"}
                    {item.category ? ` (${item.category})` : ""}
                    {item.action_type && (
                      <span className="ms-1 text-muted" style={{ fontSize: "0.85em" }}>
                        [{actionTypeAbbr(item.action_type)}]
                      </span>
                    )}
                  </td>
                  <td>{item.quantity || 0}</td>
                  <td>{parseFloat(item.weight || 0).toFixed(3)}</td>
                  <td>₹{parseFloat(item.rate || 0).toFixed(2)}</td>
{hasMaking && <td>{formatOrDash(item.making_charges, 3)}</td>}
{hasWastage && <td>{formatOrDash(item.making_wastage, 3)}</td>}
{hasStoneValue && <td>{formatOrDash(item.stone_value, 2)}</td>}
{hasDiscount && (
  <td>{!isExchange ? `₹${itemTD.toFixed(2)}` : "-"}</td>
)}
                  <td className={isExchange ? "text-danger" : ""}>
                    {isExchange
                      ? `-₹${itemAmount.toFixed(2)}`
                      : `₹${(itemAmount - itemTD).toFixed(2)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="table-footer">
              <th colSpan="2">Total</th>
              <th>{totalQty}</th>
              <th>{totalWeight.toFixed(3)}</th>
              <th>-</th>
             {hasMaking && <th>{formatOrDash(totalMaking, 3)}</th>}
    {hasWastage && <th>{formatOrDash(totalWastage, 3)}</th>}
    {hasStoneValue && <th>{formatOrDash(totalStoneValue, 2)}</th>}
    {hasDiscount && <th>₹{totalTD.toFixed(2)}</th>}
    <th>{formatSigned(netTotalBeforeTax)}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Totals + Terms */}
      <div className="details-section">
        <div className="customer-info">
          <div className="amount-in-words">
            <p><strong>Rupees:</strong> {numberToWords(grandTotal)}</p>
          </div>
          <p><strong>*Terms &amp; Conditions</strong></p>
          <p className="paragraph-cont">
            All jewellery items sold by Tirupur Jewellery are guaranteed to be
            genuine and accurately metal purity and gemstone specifications.
          </p>
        </div>
        <div className="invoice-info">
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-row"><span>Sub Total</span><span>{formatSigned(netTotalBeforeTax)}</span></div>
              {taxPercentage > 0 && (
                <div className="total-row"><span>GST ({taxPercentage}%)</span><span>₹{taxAmount.toFixed(2)}</span></div>
              )}
              {/* <div className="total-row"><span>Net Amount</span><span>{formatSigned(netTotalBeforeTax)}</span></div> */}
              {roundOff !== 0 && (
                <div className="total-row"><span>Round Off</span><span>₹{roundOff.toFixed(2)}</span></div>
              )}
              <div className="total-row highlight"><span>Grand Total</span><span>{formatSigned(grandTotal)}</span></div>
              <div className="total-row"><span>Payment Type</span><span>{paymentType}</span></div>
              <div className="total-row"><span>Paid</span><span>₹{paidAmount.toFixed(2)}</span></div>
              
              {/* Show Balance only if it exists */}
              {showBalance && (
                <div className="total-row highlight-balance">
                  <span>Balance</span>
                  <span>₹{balance.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <hr className="divider" />

      {/* Footer */}
      <div className="footer-section">
        <div className="signatures">
          <div className="customer-sign"><p>Signature of the Customer</p></div>
          <div>
            <p className="footer-notes">--- Thank You Visit Again ---</p>
            <div className="alaan"><p>Developed by Aalan Tech Soft - Salem</p></div>
          </div>
          <div className="company-sign">
            <p>FOR TIRUPUR JEWELLERY</p>
            <p>Authorized Signature</p>
          </div>
        </div>
      </div>
    </div>
  );
}